import React from 'react';
import { ArrowRight, Check, X, Zap, MoveRight, Sparkles, TrendingUp, Shield } from 'lucide-react';
import { useLocaleContext } from '../context/LocaleContext';
import { getTranslation } from '../utils/translations';

const Comparison: React.FC = () => {
  const { locale } = useLocaleContext();
  // CLS FIX: Removed animateIn state and setTimeout - animations were no-ops causing layout shift

  const features = {
    en: [
      {
        title: "Formatting",
        without: "Invisible characters can break spacing, line wrapping, and alignment in Word, Google Docs, and PDFs",
        with: "Clean, predictable spacing and layout wherever you paste",
        icon: Shield
      },
      {
        title: "Code & data",
        without: "Zero-width characters corrupt code, JSON, and find/search functions",
        with: "Text behaves correctly in editors, terminals, and search",
        icon: TrendingUp
      },
      {
        title: "Copy & paste",
        without: "Hidden markers silently transfer between apps and documents",
        with: "What you copy is exactly what you get, with nothing hidden",
        icon: Sparkles
      },
      {
        title: "Privacy",
        without: "Invisible characters mean what you paste isn't exactly what you copied",
        with: "What you share should be exactly the characters you see. Cleaned text contains nothing you can't see.",
        icon: Zap
      }
    ],
    de: [
      {
        title: "Formatierung",
        without: "Unsichtbare Zeichen können Abstände, Zeilenumbrüche und Ausrichtung in Word, Google Docs und PDFs zerstören",
        with: "Saubere, vorhersehbare Abstände und ein einheitliches Layout, egal wo Sie einfügen",
        icon: Shield
      },
      {
        title: "Code & Daten",
        without: "Zeichen mit Nullbreite beschädigen Code, JSON sowie Such- und Finden-Funktionen",
        with: "Text verhält sich in Editoren, Terminals und der Suche korrekt",
        icon: TrendingUp
      },
      {
        title: "Kopieren & Einfügen",
        without: "Versteckte Markierungen wandern unbemerkt zwischen Apps und Dokumenten",
        with: "Was Sie kopieren, ist genau das, was Sie erhalten – ohne verborgene Inhalte",
        icon: Sparkles
      },
      {
        title: "Datenschutz",
        without: "Unsichtbare Zeichen bedeuten, dass das, was Sie einfügen, nicht genau dem entspricht, was Sie kopiert haben",
        with: "Was Sie teilen, sollte genau die Zeichen sein, die Sie sehen. Bereinigter Text enthält nichts, was Sie nicht sehen können.",
        icon: Zap
      }
    ],
    'pt-BR': [
      {
        title: "Formatação",
        without: "Caracteres invisíveis podem quebrar espaçamento, quebra de linha e alinhamento no Word, Google Docs e PDFs",
        with: "Espaçamento e layout limpos e previsíveis onde quer que você cole",
        icon: Shield
      },
      {
        title: "Código e dados",
        without: "Caracteres de largura zero corrompem código, JSON e funções de localizar/pesquisar",
        with: "O texto se comporta corretamente em editores, terminais e na pesquisa",
        icon: TrendingUp
      },
      {
        title: "Copiar e colar",
        without: "Marcadores ocultos passam silenciosamente entre aplicativos e documentos",
        with: "O que você copia é exatamente o que você obtém, sem nada oculto",
        icon: Sparkles
      },
      {
        title: "Privacidade",
        without: "Caracteres invisíveis significam que o que você cola não é exatamente o que você copiou",
        with: "O que você compartilha deve ser exatamente os caracteres que você vê. O texto limpo não contém nada que você não possa ver.",
        icon: Zap
      }
    ],
    'ms-MY': [
      {
        title: "Pemformatan",
        without: "Aksara tidak kelihatan boleh merosakkan jarak, pemenggalan baris dan penjajaran dalam Word, Google Docs dan PDF",
        with: "Jarak dan susun atur yang bersih dan boleh dijangka di mana sahaja anda menampal",
        icon: Shield
      },
      {
        title: "Kod & data",
        without: "Aksara lebar sifar merosakkan kod, JSON dan fungsi cari/gelintar",
        with: "Teks berfungsi dengan betul dalam editor, terminal dan carian",
        icon: TrendingUp
      },
      {
        title: "Salin & tampal",
        without: "Penanda tersembunyi berpindah secara senyap antara aplikasi dan dokumen",
        with: "Apa yang anda salin betul-betul itulah yang anda dapat, tanpa apa-apa yang tersembunyi",
        icon: Sparkles
      },
      {
        title: "Privasi",
        without: "Aksara tidak kelihatan bermakna apa yang anda tampal bukan tepat sama seperti apa yang anda salin",
        with: "Apa yang anda kongsi sepatutnya tepat sama dengan aksara yang anda lihat. Teks yang telah dibersihkan tidak mengandungi apa-apa yang anda tidak boleh lihat.",
        icon: Zap
      }
    ],
    es: [
      {
        title: "Formato",
        without: "Los caracteres invisibles pueden romper el espaciado, el ajuste de línea y la alineación en Word, Google Docs y PDF",
        with: "Espaciado y diseño limpios y predecibles dondequiera que pegues",
        icon: Shield
      },
      {
        title: "Código y datos",
        without: "Los caracteres de ancho cero corrompen el código, el JSON y las funciones de buscar/encontrar",
        with: "El texto se comporta correctamente en editores, terminales y búsquedas",
        icon: TrendingUp
      },
      {
        title: "Copiar y pegar",
        without: "Los marcadores ocultos se transfieren en silencio entre aplicaciones y documentos",
        with: "Lo que copias es exactamente lo que obtienes, sin nada oculto",
        icon: Sparkles
      },
      {
        title: "Privacidad",
        without: "Los caracteres invisibles significan que lo que pegas no es exactamente lo que copiaste",
        with: "Lo que compartes debe ser exactamente los caracteres que ves. El texto limpio no contiene nada que no puedas ver.",
        icon: Zap
      }
    ],
    fr: [
      {
        title: "Mise en forme",
        without: "Les caractères invisibles peuvent casser l'espacement, le retour à la ligne et l'alignement dans Word, Google Docs et les PDF",
        with: "Un espacement et une mise en page propres et prévisibles où que vous colliez",
        icon: Shield
      },
      {
        title: "Code et données",
        without: "Les caractères de largeur nulle corrompent le code, le JSON et les fonctions de recherche",
        with: "Le texte se comporte correctement dans les éditeurs, les terminaux et la recherche",
        icon: TrendingUp
      },
      {
        title: "Copier-coller",
        without: "Les marqueurs cachés se transfèrent discrètement entre applications et documents",
        with: "Ce que vous copiez est exactement ce que vous obtenez, sans rien de caché",
        icon: Sparkles
      },
      {
        title: "Confidentialité",
        without: "Les caractères invisibles signifient que ce que vous collez n'est pas exactement ce que vous avez copié",
        with: "Ce que vous partagez doit être exactement les caractères que vous voyez. Le texte nettoyé ne contient rien que vous ne puissiez voir.",
        icon: Zap
      }
    ],
    ja: [
      {
        title: "書式",
        without: "不可視文字がWord、Googleドキュメント、PDFで間隔・改行・配置を崩すことがある",
        with: "どこに貼り付けても、整って予測どおりの間隔とレイアウト",
        icon: Shield
      },
      {
        title: "コードとデータ",
        without: "ゼロ幅文字がコード、JSON、検索・置換機能を壊す",
        with: "エディタ、ターミナル、検索でテキストが正しく動作する",
        icon: TrendingUp
      },
      {
        title: "コピー＆ペースト",
        without: "隠しマーカーがアプリや文書間でひそかに引き継がれる",
        with: "コピーした内容がそのまま得られ、隠れたものは一切なし",
        icon: Sparkles
      },
      {
        title: "プライバシー",
        without: "不可視文字があると、貼り付けた内容がコピーした内容と正確に一致しないことがある",
        with: "共有する内容は、見えている文字そのものであるべきです。クリーニング済みのテキストには見えないものは何も含まれません。",
        icon: Zap
      }
    ],
    id: [
      {
        title: "Pemformatan",
        without: "Karakter tak terlihat dapat merusak spasi, pembungkusan baris, dan perataan di Word, Google Docs, dan PDF",
        with: "Spasi dan tata letak yang bersih serta dapat diprediksi di mana pun Anda menempel",
        icon: Shield
      },
      {
        title: "Kode & data",
        without: "Karakter lebar nol merusak kode, JSON, dan fungsi cari/temukan",
        with: "Teks berperilaku dengan benar di editor, terminal, dan pencarian",
        icon: TrendingUp
      },
      {
        title: "Salin & tempel",
        without: "Penanda tersembunyi berpindah secara diam-diam antar aplikasi dan dokumen",
        with: "Apa yang Anda salin persis seperti yang Anda dapatkan, tanpa ada yang disembunyikan",
        icon: Sparkles
      },
      {
        title: "Privasi",
        without: "Karakter tak terlihat berarti apa yang Anda tempel tidak persis sama dengan apa yang Anda salin",
        with: "Apa yang Anda bagikan seharusnya persis sama dengan karakter yang Anda lihat. Teks yang sudah dibersihkan tidak mengandung apa pun yang tidak dapat Anda lihat.",
        icon: Zap
      }
    ]
  };

  return (
    <div className="mt-16">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 px-4 py-2 rounded-lg mb-4 border border-blue-200 dark:border-blue-800">
          <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="font-semibold text-sm text-blue-700 dark:text-blue-300">
            {locale === 'de'
              ? 'Vorher & Nachher'
              : locale === 'pt-BR'
              ? 'Antes e Depois'
              : locale === 'ms-MY'
              ? 'Sebelum & Selepas'
              : locale === 'es'
              ? 'Antes y Después'
              : locale === 'fr'
              ? 'Avant et Après'
              : locale === 'ja'
              ? 'ビフォー＆アフター'
              : locale === 'id'
              ? 'Sebelum & Sesudah'
              : 'Before & After'}
          </span>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900 dark:text-white leading-tight">
          {getTranslation(locale, 'comparison.title')}
        </h2>

        <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto leading-relaxed">
          {getTranslation(locale, 'comparison.intro')}
        </p>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 items-stretch lg:items-center">
        {/* Before Card - RED THEME */}
        <div className="flex-1">
          <div className="h-full p-6 bg-gradient-to-br from-red-50 via-orange-50 to-red-100 dark:bg-gradient-to-br dark:from-red-950/30 dark:via-charcoal-900 dark:to-orange-950/20 rounded-2xl shadow-xl dark:shadow-dark-lg border-2 border-red-300 dark:border-red-800/50 professional-transition hover:shadow-2xl hover:border-red-400 dark:hover:border-red-700/70 subtle-texture relative overflow-hidden">
            {/* Decorative gradient orbs - visible only in dark mode */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-orange-500/20 to-red-500/20 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>

            <div className="relative h-full flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-red-200 dark:bg-red-900/50 backdrop-blur-sm rounded-xl border border-red-400 dark:border-red-700">
                  <X className="w-5 h-5 text-red-700 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {getTranslation(locale, 'comparison.withWatermarks')}
                </h3>
              </div>

              <div className="space-y-3 flex-1">
                {features[locale].map((feature, index) => (
                  <div
                    key={`without-${index}`}
                    className="p-3 bg-white/80 dark:bg-red-950/20 backdrop-blur-sm rounded-lg border border-red-300 dark:border-red-800/50 hover:bg-white dark:hover:bg-red-950/30 hover:border-red-400 dark:hover:border-red-700/70 professional-transition"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 bg-red-200 dark:bg-red-900/50 rounded-md flex-shrink-0 mt-0.5">
                        <feature.icon size={14} className="text-red-700 dark:text-red-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-neutral-900 dark:text-white text-sm mb-1">
                          {feature.title}
                        </h4>
                        <p className="text-neutral-700 dark:text-neutral-300 text-xs leading-relaxed">
                          {feature.without}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Arrow Section */}
        <div className="hidden lg:flex items-center justify-center flex-shrink-0">
          <div className="p-3 bg-accent-200/50 dark:bg-white/10 backdrop-blur-sm rounded-xl border border-accent-300 dark:border-white/15 shadow-sm">
            <ArrowRight className="w-6 h-6 text-accent-700 dark:text-white" />
          </div>
        </div>

        {/* After Card - GREEN THEME */}
        <div className="flex-1">
          <div className="h-full p-6 bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:bg-gradient-to-br dark:from-green-950/30 dark:via-charcoal-900 dark:to-emerald-950/20 rounded-2xl shadow-xl dark:shadow-dark-lg border-2 border-green-300 dark:border-green-800/50 professional-transition hover:shadow-2xl hover:border-green-400 dark:hover:border-green-700/70 subtle-texture relative overflow-hidden">
            {/* Decorative gradient orbs - visible only in dark mode */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-emerald-500/20 to-green-500/20 rounded-full blur-3xl opacity-0 dark:opacity-100"></div>

            <div className="relative h-full flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-200 dark:bg-green-900/50 backdrop-blur-sm rounded-xl border border-green-400 dark:border-green-700">
                  <Check className="w-5 h-5 text-green-700 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {getTranslation(locale, 'comparison.afterRemoval')}
                </h3>
              </div>

              <div className="space-y-3 flex-1">
                {features[locale].map((feature, index) => (
                  <div
                    key={`with-${index}`}
                    className="p-3 bg-white/80 dark:bg-green-950/20 backdrop-blur-sm rounded-lg border border-green-300 dark:border-green-800/50 hover:bg-white dark:hover:bg-green-950/30 hover:border-green-400 dark:hover:border-green-700/70 professional-transition"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 bg-green-200 dark:bg-green-900/50 rounded-md flex-shrink-0 mt-0.5">
                        <feature.icon size={14} className="text-green-700 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-neutral-900 dark:text-white text-sm mb-1">
                          {feature.title}
                        </h4>
                        <p className="text-neutral-700 dark:text-neutral-300 text-xs leading-relaxed">
                          {feature.with}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-12 text-center">
        <div className="inline-flex items-center gap-2 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-lg border border-green-200 dark:border-green-800">
          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="font-semibold text-sm text-green-700 dark:text-green-300">
            {locale === 'de'
              ? 'Bereit für sauberen Text?'
              : locale === 'pt-BR'
              ? 'Pronto para texto limpo?'
              : locale === 'ms-MY'
              ? 'Bersedia untuk teks bersih?'
              : locale === 'es'
              ? '¿Listo para texto limpio?'
              : locale === 'fr'
              ? 'Prêt pour un texte propre ?'
              : locale === 'ja'
              ? 'クリーンなテキストの準備はできましたか？'
              : locale === 'id'
              ? 'Siap untuk teks bersih?'
              : 'Ready for clean text?'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Comparison;